import React, { useEffect } from "react";
import { useTheme, ThemeName } from "../../util/themes";
import { getConfiguredThemeName } from "../../util/themes/ThemeProvider";

interface ThemeWrapperProps {
  children: React.ReactNode;
}

export function ThemeWrapper({ children }: ThemeWrapperProps) {
  const theme = useTheme();
  const themeName = getConfiguredThemeName();

  useEffect(() => {
    document.documentElement.style.backgroundColor = theme.colors.background.default;
    document.documentElement.style.color = theme.colors.text.primary;

    document.body.style.backgroundColor = theme.colors.background.default;
    document.body.style.color = theme.colors.text.primary;
    document.body.style.fontFamily = theme.typography.fontFamily.primary;

    const style = document.createElement("style");
    style.id = "theme-styles";
    
    const isDarkTheme = themeName === ThemeName.DARK;
    
    style.innerHTML = `
      body, html {
        background-color: ${theme.colors.background.default};
        color: ${theme.colors.text.primary};
        font-family: ${theme.typography.fontFamily.primary};
      }


      *, *::before, *::after {
        font-family: ${theme.typography.fontFamily.primary} !important;
      }

      .MuiTypography-root,
      .MuiButton-root,
      .MuiInputBase-root,
      .MuiFormLabel-root,
      .MuiMenuItem-root,
      .MuiChip-root {
        font-family: ${theme.typography.fontFamily.primary} !important;
      }

      .MuiAppBar-root {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
      }

      .MuiToolbar-root {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
      }

      .MuiAppBar-root .MuiIconButton-root {
        color: ${theme.colors.text.primary};
      }

      .MuiAppBar-root .MuiIconButton-root svg,
      .MuiAppBar-root .MuiSvgIcon-root {
        color: ${theme.colors.text.primary};
        stroke: ${theme.colors.text.primary};
      }

      .MuiIconButton-root,
      .MuiButtonBase-root,
      .MuiButton-root {
        color: ${theme.colors.text.primary};
      }

      .MuiIconButton-root svg,
      .MuiButtonBase-root svg,
      .MuiButton-root svg {
        color: ${theme.colors.text.primary};
        fill: ${theme.colors.text.primary};
        stroke: ${theme.colors.text.primary};
      }

      a {
        color: ${theme.colors.accent};
        transition: color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      a:hover {
        ${isDarkTheme ? `color: #90caf9;` : ''}
      }

      input, textarea, select {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
        border-color: ${theme.colors.greyLight};
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      input::placeholder, textarea::placeholder {
        color: ${theme.colors.text.secondary};
      }

      input:focus, textarea:focus, select:focus {
        outline: none;
        border-color: ${theme.colors.accent};
        ${isDarkTheme ? `background-color: rgba(255, 255, 255, 0.08);` : ''}
      }

      .MuiTextField-root .MuiOutlinedInput-root {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
      }

      .MuiOutlinedInput-root {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
      }

      .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.greyLight};
      }

      .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.greyMedium};
      }

      .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: ${theme.colors.accent};
      }

      .MuiInputLabel-root {
        color: ${theme.colors.text.secondary};
      }

      .MuiInputLabel-root.Mui-focused {
        color: ${theme.colors.accent};
      }

      .MuiInputBase-input {
        color: ${theme.colors.text.primary};
      }

      .MuiFormHelperText-root {
        color: ${theme.colors.text.secondary};
      }

      .MuiSelect-select {
        color: ${theme.colors.text.primary};
        background-color: ${theme.colors.background.paper};
      }

      .MuiInputBase-input::placeholder {
        color: ${theme.colors.text.secondary};
        opacity: 1;
      }

      .MuiPaper-root {
        background-color: ${theme.colors.background.paper};
        color: ${theme.colors.text.primary};
      }

       .MuiCard-root {
         background-color: ${theme.colors.background.paper};
         color: ${theme.colors.text.primary};
       }

       /* MUI Drawer styling */
       .MuiDrawer-paper {
         background-color: ${theme.colors.background.default};
         color: ${theme.colors.text.primary};
       }


      ::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      ::-webkit-scrollbar-track {
        background: ${theme.colors.background.paper};
      }

      ::-webkit-scrollbar-thumb {
        background: ${theme.colors.greyMedium};
        border-radius: 4px;
      }

      ::-webkit-scrollbar-thumb:hover {
        background: ${theme.colors.secondary};
      }

      ${isDarkTheme ? `
        .MuiButton-root {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .MuiButton-contained {
          background-color: #444c56;
          color: #e0e6ed;
        }

        .MuiButton-contained:hover {
          background-color: #6e7681;
          color: #e0e6ed;
        }

        .MuiButton-outlined {
          border-color: #444c56;
          color: #e0e6ed;
        }

        .MuiButton-outlined:hover {
          border-color: #6e7681;
          background-color: rgba(110, 118, 129, 0.08);
        }

        .MuiTypography-root {
          color: #e0e6ed;
        }

        .MuiTypography-h1, .MuiTypography-h2, .MuiTypography-h3, 
        .MuiTypography-h4, .MuiTypography-h5, .MuiTypography-h6 {
          color: #e0e6ed;
        }

        .MuiPaper-root, .MuiCard-root {
          background-color: #161b22;
          color: #e0e6ed;
        }

        .MuiDivider-root {
          border-color: #30363d;
        }

        .MuiCheckbox-root, .MuiRadio-root {
          color: #a0a9b3;
        }

        .MuiCheckbox-root.Mui-checked, .MuiRadio-root.Mui-checked {
          color: #64b5f6;
        }

        .MuiTableCell-root {
          border-color: #30363d;
          color: #e0e6ed;
        }

        .MuiTableHead-root .MuiTableCell-root {
          background-color: #0d1117;
          color: #e0e6ed;
        }

        .MuiPaginationItem-root {
          color: #e0e6ed;
          border-color: #30363d;
          background-color: #161b22;
        }

        .MuiPaginationItem-root.Mui-selected {
          background-color: #444c56;
          color: #e0e6ed;
        }

        .MuiPaginationItem-root:hover {
          background-color: #30363d;
        }

        .MuiChip-root {
          background-color: #30363d;
          color: #e0e6ed;
        }

        .MuiTooltip-tooltip {
          background-color: #30363d;
          color: #e0e6ed;
        }

        .MuiMenu-paper, .MuiPopover-paper {
          background-color: #161b22;
          color: #e0e6ed;
        }

        .MuiMenuItem-root {
          color: #e0e6ed;
        }

        .MuiMenuItem-root:hover {
          background-color: #30363d;
        }

        .MuiMenuItem-root.Mui-selected {
          background-color: #30363d;
        }
      ` : ''}
    `;

    const oldStyle = document.getElementById("theme-styles");
    if (oldStyle) {
      oldStyle.remove();
    }

    document.head.appendChild(style);

    return () => {
      const style = document.getElementById("theme-styles");
      if (style) {
        style.remove();
      }
    };
  }, [theme, themeName]);

  return <>{children}</>;
}














